import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { canManagePaymentGateway } from "../../components/nav";
import { newIdempotencyKey } from "../../api/pinOrders";
import { WALLET_KIND_LABEL, WALLET_STATUS_LABEL, signedPaise } from "../../api/wallet";
import { formatInrPaise } from "../../domain/tiers";
import { fundState, getSmsFund, getSmsGateway, packsPaise, recordSmsTopup } from "../../api/smsGateway";

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/**
 * SMS fund (owner 2026-10-30): the operator's prepaid balance with AnkhOS for SMS
 * to its subscribers through the AnkhOS gateway. Empty, SMS keep going for the
 * grace period and the cost is taken from the next payment; after that only login
 * and verification codes go out.
 */
export function SmsFundScreen() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const qc = useQueryClient();
  const enabled = Boolean(identity.token && ns);
  const q = useQuery({ queryKey: ["smsfund", ns], enabled, queryFn: ({ signal }) => getSmsFund(identity, ns, signal) });
  // The grace days sit on the gateway overview, which only the owners read.
  const owner = canManagePaymentGateway(identity.roles ?? []);
  const overview = useQuery({ queryKey: ["smsgateway", ns], enabled: enabled && owner, queryFn: ({ signal }) => getSmsGateway(identity, ns, signal) });
  const [packs, setPacks] = useState("1");
  const [reference, setReference] = useState("");
  const [key, setKey] = useState(() => newIdempotencyKey());
  const f = q.data;
  const packPaise = f ? f.packnetpaise + f.packgstpaise : 0;
  const [starter, setStarter] = useState(false);
  const starterPaise = f?.starterpaise ?? 0;
  const useStarter = starter && starterPaise > 0;
  const paise = useStarter ? starterPaise : packsPaise(packs, packPaise);
  const record = useMutation({
    mutationFn: () => recordSmsTopup(identity, ns, { amountpaise: paise as number, paymentreference: reference, idempotencykey: key }),
    onSuccess: () => {
      setPacks("1");
      setStarter(false);
      setReference("");
      setKey(newIdempotencyKey());
      void qc.invalidateQueries({ queryKey: ["smsfund", ns] });
    },
  });

  const state = f ? fundState(f.balancepaise, f.shortsince, overview.data?.fund_grace_days ?? null, new Date()) : { kind: "ok" as const };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="h5" component="h1">SMS fund</Typography>
        <Typography variant="body2" color="text.secondary">
          Pays for SMS to your subscribers through the AnkhOS gateway. Login and verification codes are free, and so is anything sent through your own gateway.
        </Typography>
      </Box>
      {q.isError && <Alert severity="error">{errText(q.error)}</Alert>}
      {state.kind === "grace" && (
        <Alert severity="warning">
          Your SMS fund is short. SMS are still going out and the amount due is taken from your next payment.
          {state.stopsOn ? ` Add funds by ${state.stopsOn.toLocaleDateString("en-IN")} or only login and verification codes will go out.` : " Add funds soon or only login and verification codes will go out."}
        </Alert>
      )}
      {state.kind === "stopped" && (
        <Alert severity="error">Your SMS fund is empty and the grace period is over. Only login and verification codes are going out. Add funds to send the rest.</Alert>
      )}
      {f && !f.hasbillingemail && (
        <Alert severity="warning">No billing email is on your profile, so we cannot email your SMS invoices. Add one in your profile.</Alert>
      )}
      {f && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={4} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">Balance</Typography>
              <Typography variant="h6" color={f.balancepaise < 0 ? "error" : undefined}>{formatInrPaise((f.balancepaise) / 100)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Waiting for confirmation</Typography>
              <Typography variant="h6">{formatInrPaise((f.pendingpaise) / 100)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Price per SMS part</Typography>
              <Typography variant="h6">
                {f.pricepaise > 0 ? `${formatInrPaise((f.pricepaise) / 100)}${f.priceincludesgst ? " including GST" : " plus GST"}` : "Not charged yet"}
              </Typography>
            </Box>
          </Stack>
          <Typography variant="caption" color="text.secondary">A long SMS counts as more than one part.</Typography>
        </Paper>
      )}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Record a payment to AnkhOS</Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {f ? `One pack is ${f.packmessages.toLocaleString("en-IN")} messages for ${formatInrPaise(packPaise / 100)}${f.packgstpaise > 0 ? " including GST" : ""}. A long SMS counts as more than one message. ` : ""}The money is added once AnkhOS confirms it, and the invoice is emailed to you then.
        </Typography>
        {record.isError && <Alert severity="error" sx={{ mb: 1 }}>{errText(record.error)}</Alert>}
        {starterPaise > 0 && (
          <FormControlLabel
            control={<Checkbox checked={starter} onChange={(e) => setStarter(e.target.checked)} />}
            label={`First top-up only: pay ${formatInrPaise(starterPaise / 100)}${f && f.packgstpaise > 0 ? " including GST" : ""} instead of a full pack`}
          />
        )}
        <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="flex-start">
          {!useStarter && <TextField size="small" label="Packs" value={packs} onChange={(e) => setPacks(e.target.value)} error={typeof paise === "string"} helperText={typeof paise === "string" ? paise : <Box component="span" sx={{ bgcolor: "success.main", color: "success.contrastText", fontWeight: 700, fontSize: "0.875rem", px: 1, py: 0.25, borderRadius: 1 }}>Pay {formatInrPaise(paise / 100)}</Box>} />}
          <TextField size="small" label="UTR or reference" value={reference} onChange={(e) => setReference(e.target.value)} helperText=" " />
          <Button variant="contained" disabled={!f || typeof paise === "string" || reference.trim() === "" || record.isPending} onClick={() => record.mutate()}>Record payment</Button>
        </Stack>
      </Paper>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="SMS fund entries">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Entry</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Parts or reference</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(f?.entries ?? []).length === 0 && <TableRow><TableCell colSpan={5}>Nothing yet.</TableCell></TableRow>}
            {(f?.entries ?? []).map((e) => (
              <TableRow key={e.id}>
                <TableCell>{new Date(e.createdat).toLocaleString("en-IN")}</TableCell>
                <TableCell>{WALLET_KIND_LABEL[e.kind]}</TableCell>
                <TableCell>{WALLET_STATUS_LABEL[e.status]}</TableCell>
                <TableCell>{e.kind === "deduction" ? (e.quantity ?? "") : (e.paymentreference ?? "")}</TableCell>
                <TableCell align="right">{formatInrPaise((signedPaise(e)) / 100)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
